import { CModal, CSpinner } from "@coreui/react";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useCitiesQuery } from "../../hooks/Cities/useCitiesQuery";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import CityTable from "./CityTable";
import NewCity from "./NewCity";
import { Chip } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";
import SearchBox from "../../components/ui/SearchBox";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;

type StatusFilter = "all" | "active" | "inactive";

export default function Cities() {
  const [currentPage, setCurrentPage] = useState(1);
  const [addVisible, setAddVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const isActiveParam =
    statusFilter === "all" ? undefined : statusFilter === "active";

  const { Cities, fetchingCities, deleteCity, mutating } =
    useCitiesQuery(isActiveParam);

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  // Search or status change goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const filtered = useMemo(
    () =>
      (Cities ?? []).filter((city: any) =>
        city.label?.toLowerCase()?.includes(searchQuery),
      ),
    [Cities, searchQuery],
  );

  // Derived value, no state or effect needed
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = usePaginatedContent(currentPage, PAGE_SIZE, filtered);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deleteCity(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = Cities?.length ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count + add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Villes</h5>
            {Cities && (
              <Chip tone="warning">
                {total} {total > 1 ? "Villes" : "Ville"}
              </Chip>
            )}
          </div>

          <button
            type="button"
            onClick={() => setAddVisible(true)}
            className="btn btn-primary text-white rounded-pill px-4 border-0"
          >
            + Ajouter une ville
          </button>
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Rechercher une ville"
          />

          <select
            className="form-select px-3"
            style={{ ...controlStyle, width: "auto" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actives</option>
            <option value="inactive">Inactives</option>
          </select>

          <button
            type="button"
            disabled={mutating || selectedCount === 0}
            onClick={handleBulkDelete}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {mutating ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        <CModal
          alignment="center"
          visible={addVisible}
          onClose={() => setAddVisible(false)}
        >
          <NewCity onAccept={() => {}} onClose={() => setAddVisible(false)} />
        </CModal>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={fetchingCities}
            render={() =>
              paginated?.length > 0 ? (
                <CityTable
                  Cities={paginated as City[]}
                  CitiesToBeDeletedList={dispatch}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={totalPages}
          pageSize={PAGE_SIZE}
        />
      </section>
    </AdminRoute>
  );
}
